import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Container, Row } from 'reactstrap';
import { faBorderAll } from '@fortawesome/free-solid-svg-icons';
import { useLocation, useNavigate } from 'react-router';
import {
  GrievanceFilterGroup,
  GrievanceItem,
  QuerySubGroup,
  GrievanceFilter,
  QueryGroup,
} from '../../services/types/Grievance';
import getCells from './Cells';
import {
  PageHeader,
  Section,
  SpinnerArea,
  TabBar,
  CustomDropDown,
  Modal,
} from '../../shared/components';
import { findMapped, capitalize } from '../../shared/misc/utils';
import { getHiddenColumnsForPhase } from './constants';
import useServiceReducer from '../../shared/hooks/useServiceReducer';
import reducer from './reducer';
import Context from '../App/MainProvider/context';

import ColumnChecks from '../Operations/ColumnChecks';
import grievanceService from '../../services/grievanceService';
import UIKitTable from '../../shared/UIKITComponents/Table';
import UIKitTablePagination from '../../shared/UIKITComponents/TablePagination';
import UIKitButton from '../../shared/UIKITComponents/Button';
import UIKitIcon from '../../shared/UIKITComponents/Icon';
import UIKitLink from '../../shared/UIKITComponents/Link';
import Badge from '../../shared/UIKITComponents/Badge';
import UIKitFilterGroup from '../../shared/UIKITComponents/FilterGroup';
import useService from '../../shared/hooks/useService';
import UIKitDropDown from '../../shared/UIKITComponents/DropDown';

const GrievancesGrid: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const queryString = new URLSearchParams(location.search);
  const q = (key: string) => queryString.get(key);
  const operationId = q('operationId') ? +(q('operationId') || 0) : undefined;
  const [showRationaleModal, setShowRationaleModal] = useState(false);
  const [currentRationaleType, setcurrentRationaleType] = useState<string>('rationale');
  const [currentRationaleText, setCurrentRationaleText] = useState<string>();
  const [hasAttempted, setHasAttempted] = useState(false);

  const showRationale = (rationalType: string, rationaleText: string) => {
    setcurrentRationaleType(rationalType);
    setCurrentRationaleText(rationaleText);
    setShowRationaleModal(true);
  };

  const [selectedQueryGroup, setSelectedQueryGroup] = useState<GrievanceFilterGroup>({
    queryGroup: QueryGroup.InProgress,
    querySubGroups: [
      QuerySubGroup.All,
      QuerySubGroup.Applicability,
      QuerySubGroup.AssessmentAndResponse,
      QuerySubGroup.Monitoring,
    ],
  });

  const { service, inSuperUserMode, inGuidanceServiceMode, user } = useContext(Context);

  const canRegister =
    inSuperUserMode() ||
    user.grievanceRole === 'GPC' ||
    user.grievanceRole === 'GPT' ||
    user.isGrievanceGH;

  const initialHidenColumns = getHiddenColumnsForPhase(
    `${QueryGroup.InProgress}-${QuerySubGroup.All}`
  );

  const initialFilter: GrievanceFilter = {
    queryGroup: QueryGroup.InProgress,
    querySubGroup: QuerySubGroup.All,
    pageNumber: 1,
    pageSize: 10,
    sortField: 'RegistrationDate',
    sortDescending: true,
    operationId,
    hiddenColumns: initialHidenColumns,
    year: undefined,
  };

  const [grievanceYears, loadingFilters] = useService(() => grievanceService.getGrievanceFilters());

  const [data, loading, filter, dispatch, , reload] = useServiceReducer(
    grievanceService.getGrievances,
    reducer,
    initialFilter
  );
  useEffect(() => {
    if (loading) {
      setHasAttempted(true);
    }

    if (hasAttempted && !loading && !data) {
      const timer = setTimeout(() => {
        navigate('/');
      }, 2000);

      return () => clearTimeout(timer);
    }

    return undefined;
  }, [loading, hasAttempted, data]);

  const [excelLoading, setExcelLoading] = useState(false);
  const [hiddenColumns, setHideColumns] = useState<string[]>(initialHidenColumns);

  const queryGroups = useMemo(
    () => [
      {
        id: 1,
        text: t('grievanceStages.ALL'),
        htmlId: 'tabAll',
        mapped: {
          queryGroup: QueryGroup.All,
          querySubGroups: [],
        },
      },
      {
        id: 2,
        text: t('grievanceStages.InProgress'),
        htmlId: 'tabInProgress',
        mapped: {
          queryGroup: QueryGroup.InProgress,
          querySubGroups: [
            QuerySubGroup.All,
            QuerySubGroup.Applicability,
            QuerySubGroup.AssessmentAndResponse,
            QuerySubGroup.Monitoring,
          ],
        },
      },
      {
        id: 3,
        text: t('grievanceStages.ClosedAndNotApplicable'),
        htmlId: 'tabClosedAndNotApplicable',
        mapped: {
          queryGroup: QueryGroup.ClosedAndNotApplicable,
          querySubGroups: [QuerySubGroup.All, QuerySubGroup.Closed, QuerySubGroup.NotApplicable],
        },
      },
    ],
    [t]
  );

  const setQueryGroup = (tabId: number) => {
    const groupMapped = findMapped(queryGroups, tabId);
    const phaseKey =
      groupMapped.queryGroup === QueryGroup.All
        ? QueryGroup.All
        : `${groupMapped.queryGroup}-${QuerySubGroup.All}`;
    setHideColumns(getHiddenColumnsForPhase(phaseKey));
    dispatch({ type: 'QUERY_GROUP', value: groupMapped.queryGroup });
    setSelectedQueryGroup(groupMapped);
  };

  const setQuerySubGroup = (subGroup: QuerySubGroup) => {
    const phaseKey = `${filter.queryGroup}-${subGroup}`;
    setHideColumns(getHiddenColumnsForPhase(phaseKey));
    dispatch({ type: 'QUERY_SUB_GROUP', value: subGroup });
  };

  const changePage = (page: number) => dispatch({ type: 'PAGE', value: page });
  const changePageSize = (size: number) => dispatch({ type: 'PAGE_SIZE', value: size });

  const setColumnOrder = (sortField: string, sortDescending: boolean) =>
    dispatch({ type: 'ORDER', column: sortField, descending: sortDescending });

  const exportExcel = () => {
    const excelColumnMapping: Record<string, string> = {
      PreviousGrievanceId: 'PreviousGrievanceNumber',
    };
    const pascalHiddenColumns = hiddenColumns.map((x) => {
      const capitalized = capitalize(x);
      return excelColumnMapping[capitalized] || capitalized;
    });
    const excelParam = {
      ...filter,
      pageSize: 0,
      pageNumber: 1,
      hiddenColumns: pascalHiddenColumns,
    };

    service(() => grievanceService.exportExcel(excelParam), { loader: setExcelLoading });
  };

  const sethiddenColumns = useCallback(
    (id: string, value: boolean) => {
      if (id === 'number' || id === 'claimantName' || id === 'operationNumber') {
        return;
      }
      if (!value) {
        setHideColumns([...hiddenColumns, id]);
      } else {
        const columns = [...hiddenColumns].filter((x) => x !== id);
        setHideColumns(columns);
      }
    },
    [hiddenColumns]
  );

  const handleSelectAllColumns = useCallback(
    (columnIds: string[], selectAll: boolean) => {
      const lockedColumns = ['number', 'claimantName', 'operationNumber'];
      const toggleableIds = columnIds.filter((id) => !lockedColumns.includes(id));
      if (selectAll) {
        setHideColumns(hiddenColumns.filter((col) => !toggleableIds.includes(col)));
      } else {
        const newHidden = Array.from(new Set([...hiddenColumns, ...toggleableIds]));
        setHideColumns(newHidden);
      }
    },
    [hiddenColumns]
  );

  const orderedCells = useMemo(
    () => getCells(showRationale, t),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [showRationale, t]
  );

  const title = `${
    operationId && data && data.list && data.list.length > 0
      ? `${data.list[0].operationNumber} / `
      : ''
  }${t('IDB Grievance Protocol')}`;

  const findSelectedQueryGroupId = () => {
    const matchedQueryGroup = queryGroups.find((x) => x.mapped.queryGroup === filter.queryGroup);

    if (matchedQueryGroup) {
      return matchedQueryGroup.id;
    }

    return 1;
  };

  useEffect(() => () => reload(), [inSuperUserMode, inGuidanceServiceMode]);

  return (
    <>
      <Container className="dashboard grievances-grid">
        <PageHeader title={title} noBack />
        <Row>
          <Section>
            {!operationId && (
              <div className="header-controls justify-content-between">
                <TabBar
                  id="queryGroupTabBar"
                  tabSelected={findSelectedQueryGroupId()}
                  setTabSelected={setQueryGroup}
                  headers={queryGroups}
                />
                <div className="details-bar">
                  <div className="content" />
                  {data && <Badge highlightGroups={data.highlightGroups} withoutCategory />}
                </div>
              </div>
            )}
            <div>
              <div className="button-container-bar">
                <div className="button-container-bar--left">
                  <span className="grv-view-label">{t('view')}:</span>
                  {selectedQueryGroup.querySubGroups.length > 0 && (
                    <UIKitFilterGroup
                      options={selectedQueryGroup.querySubGroups.map((subGroup) => ({
                        value: subGroup as string,
                        label: t(`grievanceStages.${subGroup as string}`),
                      }))}
                      selected={filter.querySubGroup as string}
                      onChange={(value) => setQuerySubGroup(value as QuerySubGroup)}
                    />
                  )}
                  {selectedQueryGroup.queryGroup === QueryGroup.All && (
                    <>
                      <UIKitFilterGroup
                        options={[
                          {
                            label: t('all'),
                            value: QuerySubGroup.All,
                          },
                        ]}
                        selected={!filter.year ? QuerySubGroup.All : ''}
                        onChange={(value) => setQuerySubGroup(value as QuerySubGroup)}
                      />
                      <UIKitDropDown
                        placeholder={t('Years')}
                        value={filter.year || null}
                        elements={(grievanceYears || []).map((year) => ({
                          id: year,
                          text: year.toString(),
                        }))}
                        onChange={(value) => dispatch({ type: 'YEAR', value })}
                        className="grv-filter-years"
                      />
                    </>
                  )}
                </div>
                <div className="button-container-bar--right">
                  {canRegister && (
                    <UIKitButton
                      key="btnNew"
                      text={t('Register New Grievance')}
                      className="mb-1 mr-4"
                      size="large"
                      onClick={() => navigate('/grievance-protocol/create')}
                    />
                  )}
                  <CustomDropDown
                    text={t('columns')}
                    icon={faBorderAll}
                    className="oper-columns grv-grid-columns"
                  >
                    <ColumnChecks<GrievanceItem>
                      cells={orderedCells}
                      removeColums={[]}
                      hideColumns={hiddenColumns}
                      setHideColumn={sethiddenColumns}
                      showSelectAll
                      onSelectAll={handleSelectAllColumns}
                    />
                  </CustomDropDown>
                  <UIKitLink
                    htmlId="btnExcel"
                    text={t('exportToExcel')}
                    onClick={exportExcel}
                    icon={<UIKitIcon name="download" />}
                    size="medium"
                    disabled={!data || data.list.length === 0}
                  />
                </div>
              </div>
              <SpinnerArea loading={loading || excelLoading || loadingFilters}>
                {data && (
                  <UIKitTable<GrievanceItem>
                    data={data.list}
                    cells={orderedCells}
                    keyFunc={(x) => x.id}
                    sortData={filter}
                    changeSort={setColumnOrder}
                    hide={hiddenColumns}
                    headerContrast="low"
                    emptyNoDesc
                    // tableHeight={filter.pageSize > 5 ? 540 : ''}
                    footerContent={
                      <UIKitTablePagination
                        pageSize={filter.pageSize}
                        pageNumber={filter.pageNumber}
                        total={data.total}
                        onChangeSize={(s) => {
                          changePageSize(s);
                        }}
                        onChangePage={(p) => changePage(p)}
                      />
                    }
                  />
                )}
              </SpinnerArea>
            </div>
          </Section>
        </Row>
      </Container>
      <Modal
        title={t(currentRationaleType)}
        visible={showRationaleModal}
        setVisible={() => setShowRationaleModal(false)}
        actions={[{ text: 'Close', action: () => setShowRationaleModal(false) }]}
        customClass="extra_wide-modal"
        onClosed={() => setShowRationaleModal(false)}
      >
        <div className="history-content flow-history mb-0 table-responsive">
          <table>
            <tbody>
              <tr>
                <td>{currentRationaleText}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </Modal>
    </>
  );
};

export default GrievancesGrid;
